// ========================================
// USER DETAILS MODAL (OpenWebUI)
// Enhanced with account disable/enable functionality
// Refactored to use shared Modal component for proper z-index stacking
// ========================================

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import Modal from './shared/Modal';
import Button from './shared/Button';
import ConfirmModal from './shared/ConfirmModal';
import { disableUserAccount, enableUserAccount, getDisabledUserStatus } from '../services/api';

/**
 * UserDetailsModal Component
 *
 * Displays detailed information about a user and provides admin controls.
 * Features:
 * - View user information (name, email, ID, role, dates)
 * - Disable/Enable user accounts
 * - Check account status (enabled/disabled)
 * - Audit logging for all actions
 *
 * @param {Object} user - User object containing user details
 * @param {Function} onClose - Callback to close the modal
 * @param {Function} onUpdate - Optional callback after account status changes
 */
const UserDetailsModal = ({ user, onClose, onUpdate }) => {
    // ========================================
    // STATE MANAGEMENT
    // ========================================
    const { t } = useTranslation();
    const { accounts } = useMsal();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    // ========================================
    // ADMIN PROTECTION
    // ========================================
    /**
     * Protected admin email addresses
     * These accounts CANNOT be disabled or modified by support staff
     * regardless of their OpenWebUI role setting
     */
    const PROTECTED_ADMIN_EMAILS = [
        'johncattrall@gmail.com',
        'ian.w+admin@aware.co.th',
        'ian.w@aware.co.th',
        'john.c@aware.co.th'
    ];

    /**
     * Check if a user is a protected admin based on their email
     * These admins cannot be managed by support staff
     */
    const isProtectedAdmin = (userEmail) => {
        if (!userEmail) return false;
        return PROTECTED_ADMIN_EMAILS.some(
            email => email.toLowerCase() === userEmail.toLowerCase()
        );
    };

    /**
     * Check if a user is an admin based on their OpenWebUI role
     * The role field from OpenWebUI API indicates admin status
     */
    const isAdminUser = (userRole) => {
        return userRole === 'admin';
    };

    /**
     * Check if the current logged-in user can manage the target user
     * - Protected admins (by email) cannot be managed by anyone
     * - OpenWebUI admins can only manage themselves
     */
    const canManageUser = () => {
        // Protected admin emails cannot be managed by anyone (including themselves via this dashboard)
        if (isProtectedAdmin(user.email)) {
            return false;
        }

        const targetIsAdmin = isAdminUser(user.role);
        const currentUserIsTargetUser = user.email?.toLowerCase() === currentUserEmail?.toLowerCase();

        // If target is an OpenWebUI admin, only allow if it's the current user managing themselves
        if (targetIsAdmin) {
            return currentUserIsTargetUser;
        }

        // Non-admin users can be managed by anyone with dashboard access
        return true;
    };

    // eslint-disable-next-line no-unused-vars
    const userIsAdmin = isAdminUser(user.role);
    // eslint-disable-next-line no-unused-vars
    const userIsProtected = isProtectedAdmin(user.email);
    const canManage = canManageUser();

    const [isDisabling, setIsDisabling] = useState(false);
    const [isEnabling, setIsEnabling] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [isAccountDisabled, setIsAccountDisabled] = useState(false);
    const [disabledInfo, setDisabledInfo] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null });
    const [error, setError] = useState(null);

    // ========================================
    // USER DATA EXTRACTION
    // ========================================
    const email = user.email || 'No email';
    const userId = user.id || 'Unknown';
    const role = user.role || 'user';

    /**
     * Format Unix timestamp to readable date
     * Unix timestamp is in seconds, multiply by 1000 for JavaScript Date
     */
    const lastActive = user.last_active_at
        ? new Date(user.last_active_at * 1000).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'N/A';

    const createdAt = user.created_at
        ? new Date(user.created_at * 1000).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'N/A';

    // ========================================
    // CHECK ACCOUNT STATUS ON MOUNT
    // ========================================
    useEffect(() => {
        checkAccountStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    /**
     * Check if user account is disabled by checking backend disabled-users store
     * A user is considered disabled if they are in the disabled-users list
     * (meaning their groups were removed and stored for later restoration)
     */
    const checkAccountStatus = async () => {
        setCheckingStatus(true);

        const result = await getDisabledUserStatus(userId);

        if (result.success) {
            setIsAccountDisabled(result.isDisabled === true);
            if (result.isDisabled && result.data) {
                setDisabledInfo(result.data);
            } else {
                setDisabledInfo(null);
            }
        } else {
            // Assume enabled if we can't check
            setIsAccountDisabled(false);
            setDisabledInfo(null);
        }

        setCheckingStatus(false);
    };

    // ========================================
    // ACCOUNT MANAGEMENT HANDLERS
    // ========================================

    /**
     * Show confirmation for disable account action
     */
    const handleDisableAccount = () => {
        setConfirmModal({ isOpen: true, action: 'disable' });
    };

    /**
     * Execute disable account action
     * Sets is_active to false, effectively blocking access
     */
    const executeDisableAccount = async () => {
        setIsDisabling(true);
        setError(null);

        const result = await disableUserAccount(userId, currentUserEmail, email);

        setConfirmModal({ isOpen: false, action: null });

        if (result.success) {
            // Update local state
            setIsAccountDisabled(true);

            // Trigger refresh if callback provided
            if (onUpdate) {
                await onUpdate();
            }
        } else {
            setError(`Failed to disable account: ${result.error}`);
        }

        setIsDisabling(false);
    };

    /**
     * Show confirmation for enable account action
     */
    const handleEnableAccount = () => {
        setConfirmModal({ isOpen: true, action: 'enable' });
    };

    /**
     * Execute enable account action
     * Sets is_active to true, allowing access again
     */
    const executeEnableAccount = async () => {
        setIsEnabling(true);
        setError(null);

        const result = await enableUserAccount(userId, currentUserEmail, email);

        setConfirmModal({ isOpen: false, action: null });

        if (result.success) {
            // Update local state
            setIsAccountDisabled(false);

            // Trigger refresh if callback provided
            if (onUpdate) {
                await onUpdate();
            }
        } else {
            setError(`Failed to enable account: ${result.error}`);
        }

        setIsEnabling(false);
    };

    // ========================================
    // STYLES
    // ========================================
    const styles = {
        infoRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem 0',
            borderBottom: '1px solid var(--border)'
        },
        infoLabel: {
            color: 'var(--muted)',
            fontSize: '0.9rem'
        },
        infoValue: {
            color: 'var(--fg-primary)',
            fontWeight: '500',
            textAlign: 'right'
        },
        sectionTitle: {
            color: 'var(--fg-primary)',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '2px solid var(--signal-blue)'
        },
        statusBadge: {
            display: 'inline-block',
            padding: '0.25rem 0.6rem',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600'
        },
        adminBadge: {
            marginLeft: '0.5rem',
            fontSize: '0.7rem',
            backgroundColor: 'var(--border)',
            color: 'var(--fg-primary)',
            padding: '0.15rem 0.4rem',
            borderRadius: '4px'
        }
    };

    // ========================================
    // BUILD MODAL TITLE WITH STATUS BADGE
    // ========================================
    const modalTitle = (
        <span style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {t('userDetails.title')}
            {!checkingStatus && (
                <span
                    style={{
                        ...styles.statusBadge,
                        backgroundColor: isAccountDisabled ? 'var(--critical)' : 'var(--success)',
                        color: '#ffffff'
                    }}
                >
                    {isAccountDisabled ? t('userDetails.disabled') : t('userDetails.active')}
                </span>
            )}
        </span>
    );

    // ========================================
    // COMPONENT RENDER
    // ========================================
    return (
        <>
            <Modal
                isOpen={true}
                onClose={onClose}
                title={modalTitle}
                maxWidth="550px"
                footer={
                    <Button onClick={onClose} variant="secondary">
                        {t('common.close')}
                    </Button>
                }
            >
                {/* User Information Section */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={styles.sectionTitle}>{t('userDetails.userInfo')}</h3>

                    {/* Name */}
                    <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>{t('userDetails.name')}:</span>
                        <span style={styles.infoValue}>{user.name || t('userDetails.noName')}</span>
                    </div>

                    {/* Email */}
                    <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>{t('userDetails.email')}:</span>
                        <span style={styles.infoValue}>{email}</span>
                    </div>

                    {/* User ID */}
                    <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>{t('userDetails.userId')}:</span>
                        <span
                            style={{
                                ...styles.infoValue,
                                fontSize: '0.8125rem',
                                wordBreak: 'break-all',
                                fontFamily: 'monospace'
                            }}
                        >
                            {userId}
                        </span>
                    </div>

                    {/* Role */}
                    <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>{t('userDetails.role')}:</span>
                        <span style={styles.infoValue}>
                            {role}
                            {(role === 'admin' || userIsAdmin) && (
                                <span style={styles.adminBadge}>{t('userDetails.admin')}</span>
                            )}
                        </span>
                    </div>

                    {/* Created Date */}
                    <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>{t('userDetails.createdAt')}:</span>
                        <span style={styles.infoValue}>{createdAt}</span>
                    </div>

                    {/* Last Active Date */}
                    <div style={{ ...styles.infoRow, borderBottom: 'none' }}>
                        <span style={styles.infoLabel}>{t('userDetails.lastActive')}:</span>
                        <span style={styles.infoValue}>{lastActive}</span>
                    </div>
                </div>

                {/* Account Status Warning - Shown if disabled */}
                {!checkingStatus && isAccountDisabled && (
                    <div
                        style={{
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: 'var(--critical-bg)',
                            border: '1px solid var(--critical)',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            color: 'var(--critical)'
                        }}
                    >
                        <strong>{t('userDetails.accountDisabled')}:</strong> {t('userDetails.disabledMessage')}
                        {disabledInfo && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.9 }}>
                                {disabledInfo.disabledBy && (
                                    <div>{t('userDetails.disabledBy')}: {disabledInfo.disabledBy}</div>
                                )}
                                {disabledInfo.disabledAt && (
                                    <div>{t('userDetails.disabledOn')}: {new Date(disabledInfo.disabledAt).toLocaleString()}</div>
                                )}
                                {disabledInfo.groups && disabledInfo.groups.length > 0 && (
                                    <div>{t('userDetails.originalGroups')}: {disabledInfo.groups.map(g => g.name).join(', ')}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div
                        style={{
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: 'var(--critical-bg)',
                            border: '1px solid var(--critical)',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            color: 'var(--critical)'
                        }}
                    >
                        <strong>{t('common.error')}:</strong> {error}
                    </div>
                )}

                {/* Account Management Section */}
                <div>
                    <h3 style={styles.sectionTitle}>{t('userDetails.accountManagement')}</h3>

                    {checkingStatus ? (
                        <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--muted)' }}>
                            {t('userDetails.checkingStatus')}
                        </div>
                    ) : !canManage ? (
                        /* Admin protection message - shown when user cannot manage this account */
                        <div style={{
                            padding: '1rem',
                            backgroundColor: 'var(--accent-bg)',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            color: 'var(--fg-secondary)',
                            textAlign: 'center'
                        }}>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <span style={{
                                    backgroundColor: 'var(--border)',
                                    color: 'var(--fg-primary)',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                }}>
                                    {t('userDetails.adminAccount')}
                                </span>
                            </div>
                            <p style={{ margin: 0 }}>
                                {t('userDetails.adminProtectionMessage')}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Information about the action */}
                            <div style={{
                                marginBottom: '1rem',
                                fontSize: '0.9rem',
                                color: 'var(--fg-secondary)',
                                lineHeight: '1.6'
                            }}>
                                {isAccountDisabled ? (
                                    <p style={{ margin: 0 }}>
                                        <strong style={{ color: 'var(--fg-primary)' }}>{t('userDetails.enableAccount')}:</strong> {t('userDetails.enableDescription')}
                                    </p>
                                ) : (
                                    <p style={{ margin: 0 }}>
                                        <strong style={{ color: 'var(--fg-primary)' }}>{t('userDetails.disableAccount')}:</strong> {t('userDetails.disableDescription')}
                                    </p>
                                )}
                            </div>

                            {/* Action Button */}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                {isAccountDisabled ? (
                                    <Button
                                        onClick={handleEnableAccount}
                                        variant="primary"
                                        disabled={isEnabling}
                                        style={{
                                            minWidth: '160px',
                                            backgroundColor: 'var(--success)'
                                        }}
                                    >
                                        {isEnabling ? t('userDetails.enablingAccount') : t('userDetails.enableAccount')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleDisableAccount}
                                        variant="danger"
                                        disabled={isDisabling}
                                        style={{ minWidth: '160px' }}
                                    >
                                        {isDisabling ? t('userDetails.disablingAccount') : t('userDetails.disableAccount')}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            {/* Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, action: null })}
                onConfirm={confirmModal.action === 'disable' ? executeDisableAccount : executeEnableAccount}
                title={confirmModal.action === 'disable' ? t('userDetails.disableUserAccount') : t('userDetails.enableUserAccount')}
                message={confirmModal.action === 'disable'
                    ? t('userDetails.disableConfirmMessage', { name: user.name || email })
                    : t('userDetails.enableConfirmMessage', { name: user.name || email })
                }
                details={confirmModal.action === 'disable'
                    ? t('userDetails.disableConfirmDetails')
                    : t('userDetails.enableConfirmDetails')
                }
                variant={confirmModal.action === 'disable' ? 'danger' : 'primary'}
                confirmLabel={confirmModal.action === 'disable' ? t('userDetails.disableAccount') : t('userDetails.enableAccount')}
                loading={isDisabling || isEnabling}
            />
        </>
    );
};

export default UserDetailsModal;

